/**
 * Terms & Conditions gate for Guocali (Horizon 19-09-26).
 *
 * CART (page + drawer): inline required checkbox above "Check out"
 *   ([data-cart-terms-checkbox], snippets/cart-terms-gate.liquid mode 'inline').
 *   Check out and the cart express buttons are blocked until it is ticked; clicking
 *   them unticked highlights the checkbox. Ticking saves the acceptance.
 * PRODUCT PAGE: Buy it now / Shop Pay / PayPal always sit under a transparent overlay
 *   (blocks/accelerated-checkout.liquid). Not accepted yet -> popup first. Then the
 *   customer goes to checkout through a cart permalink (/cart/VARIANT:QTY?attributes[..])
 *   that carries the acceptance attributes, so Buy it now orders are recorded too
 *   (Shopify's native Buy it now drops cart attributes).
 * Any checkout button with no inline checkbox nearby also falls back to the popup.
 * On "Accept & continue" the acceptance is saved as cart attributes, which Shopify copies
 * onto the order ("Additional details" in the order admin):
 *   - Terms & Conditions : Accepted
 *   - Terms accepted at  : 2026-09-26 14:32:05 UTC+02:00 (Europe/Paris)
 *   - Terms accepted via : Cart | Product page  (+ " (pre-selected)" if the box was pre-ticked
 *                          by the theme setting and left as is)
 * then the original action continues (checkout submit, or a click on the payment button).
 * Customers who already accepted (cart attribute present) are never asked again.
 */

const ATTR_STATUS = 'Terms & Conditions';
const ATTR_TIME = 'Terms accepted at';
const ATTR_SOURCE = 'Terms accepted via';
const INLINE_SELECTOR = '[data-cart-terms-checkbox]';
const SUMMARY_SELECTOR = '.cart-summary, .cart-drawer__summary, .cart__summary, cart-drawer, .cart-drawer';
const CHECKOUT_SELECTOR = '.cart__checkout-button, button[name="checkout"], input[name="checkout"]';
const SAVE_TIMEOUT_MS = 2500;

class TermsGate {
  /** @type {boolean | null} */
  static #accepted = null;
  static #record = true;
  static #precheck = false;

  /** @type {HTMLDialogElement | null} */
  static #dialog = null;

  /** @type {null | { source: string, onAccept: () => void }} */
  static #request = null;

  static #checkoutBypass = false;
  /** @type {string} */
  static #acceptedAt = '';
  /** @type {string} */
  static #acceptedVia = '';
  static #precheckRecorded = false;

  static init() {
    TermsGate.#readConfig();
    TermsGate.#maybeRecordPrecheck();
    TermsGate.#processInline();
    TermsGate.#processOverlays();

    new MutationObserver(() => {
      TermsGate.#readConfig();
      TermsGate.#processInline();
      TermsGate.#processOverlays();
    }).observe(document.body, { childList: true, subtree: true });

    // Standard "Check out" button (click + keyboard submit). Capture phase so we run
    // before the theme's own handlers.
    document.addEventListener('click', TermsGate.#onDocumentClick, true);
    document.addEventListener('submit', TermsGate.#onDocumentSubmit, true);
  }

  static #template() {
    return /** @type {HTMLTemplateElement | null} */ (document.querySelector('template[data-terms-gate-template]'));
  }

  static #readConfig() {
    const template = TermsGate.#template();
    if (!template) return;

    TermsGate.#record = template.dataset.record !== 'false';
    TermsGate.#precheck = template.dataset.precheck === 'true';

    if (!TermsGate.#acceptedAt && template.dataset.acceptedAt) {
      TermsGate.#acceptedAt = template.dataset.acceptedAt;
      TermsGate.#acceptedVia = template.dataset.acceptedVia || '';
    }

    // First read: server state (cart attribute) or a pre-ticked inline checkbox.
    if (TermsGate.#accepted === null) {
      const inlineChecked = Array.from(TermsGate.#inlineBoxes()).some((box) => box.checked);
      TermsGate.#accepted = template.dataset.accepted === 'true' || inlineChecked;
    }
  }

  static #inlineBoxes() {
    return /** @type {NodeListOf<HTMLInputElement>} */ (document.querySelectorAll(INLINE_SELECTOR));
  }

  /** @param {Element} element */
  static #inlineFor(element) {
    return /** @type {HTMLInputElement | null} */ (
      element.closest(SUMMARY_SELECTOR)?.querySelector(INLINE_SELECTOR) ?? null
    );
  }

  /* ---------------- Inline cart checkbox ---------------- */

  static #processInline() {
    TermsGate.#inlineBoxes().forEach((box) => {
      if (TermsGate.#accepted !== null && box.checked !== TermsGate.#accepted) box.checked = TermsGate.#accepted;
      TermsGate.#applyValidity(box);

      if (box.dataset.termsBound) return;
      box.dataset.termsBound = 'true';
      box.addEventListener('change', () => TermsGate.#onInlineChange(box));
    });
  }

  /** @param {HTMLInputElement} box */
  static #applyValidity(box) {
    box.setCustomValidity(box.checked ? '' : box.dataset.cartTermsMessage || '');
    if (box.checked) box.closest('.cart-terms-gate')?.classList.remove('cart-terms-gate--invalid');
  }

  /** @param {HTMLInputElement} source */
  static #onInlineChange(source) {
    TermsGate.#accepted = source.checked;
    TermsGate.#inlineBoxes().forEach((box) => {
      box.checked = source.checked;
      TermsGate.#applyValidity(box);
    });
    TermsGate.#syncOverlays();
    if (TermsGate.#record) TermsGate.#save(source.checked, 'Cart');
  }

  /** @param {HTMLInputElement} box */
  static #flagInline(box) {
    const wrapper = box.closest('.cart-terms-gate');
    wrapper?.classList.remove('cart-terms-gate--invalid');
    void (/** @type {HTMLElement | null} */ (wrapper)?.offsetWidth);
    wrapper?.classList.add('cart-terms-gate--invalid');
    box.focus({ preventScroll: false });
    box.reportValidity?.();
  }

  /** A theme-pre-ticked box is recorded once, as "Cart (pre-selected)", when the cart has items. */
  static #maybeRecordPrecheck() {
    if (TermsGate.#precheckRecorded || !TermsGate.#record) return;
    const template = TermsGate.#template();
    if (!template || template.dataset.accepted === 'true') return;

    const box = Array.from(TermsGate.#inlineBoxes()).find(
      (b) => b.checked && b.dataset.termsPrecheck === 'true' && b.hasAttribute('data-terms-cart-has-items')
    );
    if (!box) return;

    TermsGate.#precheckRecorded = true;
    TermsGate.#save(true, 'Cart (pre-selected)');
  }

  static #isGated() {
    return Boolean(TermsGate.#template()) && TermsGate.#accepted !== true;
  }

  /* ---------------- Checkout button ---------------- */

  /** @param {MouseEvent} event */
  static #onDocumentClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest?.(CHECKOUT_SELECTOR) ?? null);
    if (!button || button.disabled || TermsGate.#checkoutBypass) return;

    const inline = TermsGate.#inlineFor(button);
    if (inline) {
      if (inline.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      TermsGate.#flagInline(inline);
      return;
    }

    if (!TermsGate.#isGated()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    TermsGate.#open('Cart', () => TermsGate.#continueCheckout(button));
  };

  /** @param {SubmitEvent} event */
  static #onDocumentSubmit = (event) => {
    const submitter = /** @type {HTMLElement | null} */ (event.submitter ?? null);
    if (!submitter?.matches?.(CHECKOUT_SELECTOR) || TermsGate.#checkoutBypass) return;

    const inline = TermsGate.#inlineFor(submitter);
    if (inline) {
      if (inline.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      TermsGate.#flagInline(inline);
      return;
    }

    if (!TermsGate.#isGated()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    TermsGate.#open('Cart', () => TermsGate.#continueCheckout(/** @type {HTMLButtonElement} */ (submitter)));
  };

  /** @param {HTMLButtonElement} button */
  static #continueCheckout(button) {
    const formId = button.getAttribute('form');
    const form = /** @type {HTMLFormElement | null} */ (
      button.form || (formId ? document.getElementById(formId) : null)
    );

    TermsGate.#checkoutBypass = true;
    try {
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(button);
        return;
      }
      button.click();
    } catch (error) {
      window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
    } finally {
      setTimeout(() => (TermsGate.#checkoutBypass = false), 0);
    }
  }

  /* ---------------- Express / dynamic checkout overlays ---------------- */

  static #processOverlays() {
    document.querySelectorAll('[data-cart-terms-overlay-wrapper]').forEach((wrapper) => {
      const overlay = /** @type {HTMLElement | null} */ (wrapper.querySelector('[data-cart-terms-overlay]'));
      if (!overlay) return;

      overlay.classList.toggle('cart-terms-gate__overlay--active', TermsGate.#overlayActive(overlay));

      if (overlay.dataset.termsBound) return;
      overlay.dataset.termsBound = 'true';

      /** @param {MouseEvent | KeyboardEvent} event */
      const handle = (event) => {
        const isProduct = Boolean(wrapper.closest('[data-terms-gate-scope]'));
        if (!isProduct && !TermsGate.#isGated()) return;
        event.preventDefault();
        event.stopPropagation();

        if (isProduct) {
          if (TermsGate.#isGated()) {
            TermsGate.#open('Product page', () => TermsGate.#productCheckout(wrapper));
          } else {
            TermsGate.#productCheckout(wrapper);
          }
          return;
        }

        const rect = overlay.getBoundingClientRect();
        const point =
          'clientX' in event && event.clientX
            ? { x: event.clientX, y: event.clientY }
            : { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        const inline = wrapper.closest('[data-terms-gate-scope]') ? null : TermsGate.#inlineFor(wrapper);
        if (inline) {
          TermsGate.#flagInline(inline);
          return;
        }

        const source = wrapper.closest('[data-terms-gate-scope]') ? 'Product page' : 'Cart';

        TermsGate.#open(source, () => TermsGate.#clickUnderlying(wrapper, point));
      };

      overlay.addEventListener('click', handle);
      overlay.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        handle(event);
      });
    });
  }

  static #syncOverlays() {
    document.querySelectorAll('[data-cart-terms-overlay]').forEach((overlay) => {
      overlay.classList.toggle('cart-terms-gate__overlay--active', TermsGate.#overlayActive(overlay));
    });
  }

  /** Product-page overlays stay on so every Buy it now goes through #productCheckout. */
  /** @param {Element} overlay */
  static #overlayActive(overlay) {
    if (overlay.closest('[data-terms-gate-scope]')) return true;
    return TermsGate.#isGated();
  }

  /**
   * Sends the customer straight to checkout for the selected variant + quantity using a
   * cart permalink that carries the acceptance as order attributes (Additional details).
   * @param {Element} wrapper
   */
  static #productCheckout(wrapper) {
    const form = /** @type {HTMLFormElement | null} */ (wrapper.closest('form'));
    const variantId = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'))?.value;

    if (!form || !variantId) {
      // Fallback: unlock and let Shopify's own button run.
      TermsGate.#clickUnderlying(wrapper, TermsGate.#centerOf(wrapper));
      return;
    }

    let quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    if (!quantityInput && form.id) {
      quantityInput = /** @type {HTMLInputElement | null} */ (
        document.querySelector(`input[name="quantity"][form="${form.id}"]`)
      );
    }
    const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10) || 1);

    const time = TermsGate.#acceptedAt || TermsGate.#timestamp();
    const via = TermsGate.#acceptedVia || 'Product page';
    const attributes = {
      [ATTR_STATUS]: 'Accepted',
      [ATTR_TIME]: time,
      [ATTR_SOURCE]: via.includes('Buy it now') ? via : `${via} (Buy it now)`,
    };

    const query = Object.entries(attributes)
      .map(([key, value]) => `attributes[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`)
      .join('&');

    const root = window.Shopify?.routes?.root || '/';
    window.location.href = `${root}cart/${encodeURIComponent(variantId)}:${quantity}?${query}`;
  }

  /** @param {Element} element */
  static #centerOf(element) {
    const rect = element.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  /**
   * Best effort: re-trigger the payment button the customer originally pressed.
   * If the button lives in a closed shadow root or iframe this may not fire; the buttons
   * are unlocked by then, so the customer simply taps again.
   * @param {Element} wrapper
   * @param {{ x: number, y: number }} point
   */
  static #clickUnderlying(wrapper, point) {
    requestAnimationFrame(() => {
      const direct = /** @type {HTMLElement | null} */ (
        wrapper.querySelector('.shopify-payment-button__button:not([disabled])')
      );
      if (direct) {
        direct.click();
        return;
      }

      let element = /** @type {Element | null} */ (document.elementFromPoint(point.x, point.y));
      while (element?.shadowRoot) {
        const inner = element.shadowRoot.elementFromPoint(point.x, point.y);
        if (!inner || inner === element) break;
        element = inner;
      }

      if (element && wrapper.contains(element.getRootNode() instanceof ShadowRoot ? element.getRootNode().host : element)) {
        const clickable = /** @type {HTMLElement | null} */ (element.closest('button, a, [role="button"]') || element);
        clickable?.click?.();
      }
    });
  }

  /* ---------------- Popup ---------------- */

  static #ensureDialog() {
    if (TermsGate.#dialog?.isConnected) return TermsGate.#dialog;

    const template = TermsGate.#template();
    const source = template?.content.querySelector('dialog');
    if (!source) return null;

    const dialog = /** @type {HTMLDialogElement} */ (source.cloneNode(true));
    document.body.appendChild(dialog);

    const checkbox = /** @type {HTMLInputElement} */ (dialog.querySelector('[data-terms-dialog-checkbox]'));
    const error = /** @type {HTMLElement} */ (dialog.querySelector('[data-terms-error]'));
    const label = checkbox.closest('label');

    checkbox.addEventListener('change', () => {
      checkbox.dataset.touched = 'true';
      if (checkbox.checked) {
        error.hidden = true;
        label?.classList.remove('terms-gate-dialog__label--invalid');
      }
    });

    dialog.querySelectorAll('[data-terms-cancel]').forEach((button) =>
      button.addEventListener('click', () => TermsGate.#close())
    );

    // Click on the backdrop closes the popup.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) TermsGate.#close();
    });

    dialog.addEventListener('close', () => {
      TermsGate.#request = null;
    });

    dialog.querySelector('[data-terms-accept]')?.addEventListener('click', () => TermsGate.#onAccept());

    TermsGate.#dialog = dialog;
    return dialog;
  }

  /**
   * @param {string} source
   * @param {() => void} onAccept
   */
  static #open(source, onAccept) {
    const dialog = TermsGate.#ensureDialog();
    if (!dialog) {
      onAccept();
      return;
    }

    const checkbox = /** @type {HTMLInputElement} */ (dialog.querySelector('[data-terms-dialog-checkbox]'));
    const error = /** @type {HTMLElement} */ (dialog.querySelector('[data-terms-error]'));
    checkbox.checked = TermsGate.#precheck;
    delete checkbox.dataset.touched;
    error.hidden = true;
    checkbox.closest('label')?.classList.remove('terms-gate-dialog__label--invalid');

    TermsGate.#request = { source, onAccept };

    if (!dialog.open) dialog.showModal();
    checkbox.focus({ preventScroll: true });
  }

  static #close() {
    TermsGate.#dialog?.close();
  }

  static async #onAccept() {
    const dialog = TermsGate.#dialog;
    const request = TermsGate.#request;
    if (!dialog || !request) return;

    const checkbox = /** @type {HTMLInputElement} */ (dialog.querySelector('[data-terms-dialog-checkbox]'));
    const error = /** @type {HTMLElement} */ (dialog.querySelector('[data-terms-error]'));
    const accept = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-terms-accept]'));

    if (!checkbox.checked) {
      error.hidden = false;
      checkbox.closest('label')?.classList.add('terms-gate-dialog__label--invalid');
      checkbox.focus();
      return;
    }

    const preSelected = TermsGate.#precheck && checkbox.dataset.touched !== 'true';
    const source = preSelected ? `${request.source} (pre-selected)` : request.source;

    accept.setAttribute('aria-busy', 'true');
    if (TermsGate.#record) {
      await TermsGate.#save(true, source);
    } else {
      TermsGate.#acceptedAt = TermsGate.#timestamp();
      TermsGate.#acceptedVia = source;
    }
    accept.removeAttribute('aria-busy');

    TermsGate.#accepted = true;
    TermsGate.#processInline();
    TermsGate.#syncOverlays();
    TermsGate.#close();
    request.onAccept();
  }

  /**
   * Saves (or withdraws) the acceptance on the cart. Never blocks longer than SAVE_TIMEOUT_MS.
   * @param {boolean} accepted
   * @param {string} source
   */
  static #save(accepted, source) {
    TermsGate.#acceptedAt = accepted ? TermsGate.#timestamp() : '';
    TermsGate.#acceptedVia = accepted ? source : '';

    const attributes = accepted
      ? { [ATTR_STATUS]: 'Accepted', [ATTR_TIME]: TermsGate.#acceptedAt, [ATTR_SOURCE]: source }
      : { [ATTR_STATUS]: 'Not accepted', [ATTR_TIME]: '', [ATTR_SOURCE]: '' };

    const request = fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).catch((error) => console.warn('[terms-gate] Could not save acceptance:', error));

    const timeout = new Promise((resolve) => setTimeout(resolve, SAVE_TIMEOUT_MS));
    return Promise.race([request, timeout]);
  }

  /** @returns {string} e.g. "2026-09-26 14:32:05 UTC+02:00 (Europe/Paris)" */
  static #timestamp() {
    const date = new Date();
    /** @param {number} n */
    const pad = (n) => String(n).padStart(2, '0');

    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const abs = Math.abs(offset);

    let zone = '';
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (error) {
      zone = '';
    }

    const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    const utc = `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;

    return `${day} ${time} ${utc}${zone ? ` (${zone})` : ''}`;
  }
}

if (!window.__termsGateLoaded) {
  window.__termsGateLoaded = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TermsGate.init());
  } else {
    TermsGate.init();
  }
}
